import PropTypes from 'prop-types';

import { useAppDispatch as useDispatch } from 'dux/app/hooks';

import each from 'lodash/fp/each';

import { legacyTheme, styled } from '@prose-ui/legacy';
import * as Sentry from '@sentry/nextjs';

import { Typography } from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import getMediaUrl from 'utils/getMediaUrl';

import { trackHeapEvent } from 'dux/tracking/actions';

const VideoContainer = styled.div`
  margin: 0 auto;
  width: 100%;
  text-align: center;

  video {
    width: inherit;
  }
`;

const HowToUseBlock = styled.div`
  margin-bottom: ${legacyTheme.spacing.s12};
`;

const BoldText = styled.span`
  font-weight: 600;
`;

const VideoCaptions = styled.div`
  margin: ${legacyTheme.spacing.s12} 0 ${legacyTheme.spacing.s48} 0;

  span {
    margin-bottom: ${legacyTheme.spacing.s48};
  }
`;

const HowToVideo = ({ howToCaption, productsCaption, image, url, dataClick }) => {
  const dispatch = useDispatch();
  const pauseVideos = target => {
    const videos = document.querySelectorAll('video');

    each(video => {
      if (video.tagName.toLowerCase() === 'video' && target !== video) {
        video.pause();
      }
    }, videos);
  };
  const playVideo = target => {
    Sentry.addBreadcrumb({ message: 'Video played' });

    dispatch(
      trackHeapEvent('video played', {
        videoName: target.dataset.name,
      })
    );

    pauseVideos(target);
  };
  return (
    <VideoContainer>
      <video
        controls
        data-click={dataClick}
        data-from="education-video-module"
        data-name={howToCaption}
        onError={event => {
          /**
           * As seen on Sentry, an unhandled exception can occour while pausing videos
           * This will help reducing the number of alerts
           * We listen to React's syntatic event and prevent default so no unhandled exception
           * https://reactjs.org/docs/handling-events.html
           */
          event.preventDefault();
        }}
        onPlay={({ target }) => {
          playVideo(target);
        }}
        playsInline
        poster={image?.src}
      >
        <source src={`${getMediaUrl(url)}.webm`} type="video/webm" />
        <source src={`${getMediaUrl(url)}.mp4`} type="video/mp4" />
        <track default kind="captions" />
        Your browser does not support the video tag.
      </video>

      <VideoCaptions>
        <Typography color="white" markupName="div" variant="mono3">
          <HowToUseBlock>
            <BoldText>How to: </BoldText>
            {howToCaption}
          </HowToUseBlock>
          {productsCaption && (
            <>
              <BoldText>Prose products used: </BoldText>
              <span>{productsCaption}</span>
            </>
          )}
        </Typography>
      </VideoCaptions>
    </VideoContainer>
  );
};

HowToVideo.defaultProps = {
  productsCaption: null,
  // If no specific thumbnail provided, use the default video preview
  image: null,
};

HowToVideo.propTypes = {
  howToCaption: PropTypes.string.isRequired,
  productsCaption: PropTypes.string,
  image: srcOrNextImageProp,
  url: PropTypes.string.isRequired,
  dataClick: PropTypes.string.isRequired,
};

export default HowToVideo;
